import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedCommand } from '../../../../../service'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydLabel, { hookFormFieldErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydCheckbox from '../../../../commonDesign/SydCheckbox'
import SydAutocomplete from '../../../../commonDesign/SydAutocomplete'
import SydButton from '../../../../commonDesign/Button'
import WYSIWYGEditor from '../../../WYSIWYGEditor'
import { membersToOptions, renderContactOption, editorBorderStyle } from '../shared/redtailUIHelpers'

function extractCategoryOptions (notes) {
  if (!notes?.length) return []
  const seen = new Map()
  for (const note of notes) {
    if (note.category_id && note.category && !seen.has(note.category_id)) {
      seen.set(note.category_id, { label: note.category, value: note.category_id })
    }
  }
  return [{ label: 'None', value: '' }, ...seen.values()]
}

function CreateNoteModal ({ open, onClose, config, members, existingNotes }) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()

  const familyOptions = useMemo(() => membersToOptions(members), [members])
  const categoryOptions = useMemo(() => extractCategoryOptions(existingNotes), [existingNotes])

  const defaultContact = familyOptions.length > 0 ? familyOptions[0] : null

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      body: '',
      contact: defaultContact,
      category_id: '',
      pinned: false
    }
  })

  const bodyValue = watch('body')
  const isBodyEmpty = !bodyValue || !bodyValue.trim() || bodyValue === '<p></p>' || bodyValue === '<p><br></p>'

  const handleClose = useCallback(() => {
    reset({ body: '', contact: defaultContact, category_id: '', pinned: false })
    onClose()
  }, [reset, onClose, defaultContact])

  const { mutate: submitNote, isLoading: submitting, error } = useMutation({
    mutationFn: (values) => {
      const contactId = values.contact?.value
      const body = {
        body: values.body,
        note_type: 1
      }
      if (values.category_id) {
        body.category_id = values.category_id
      }
      if (values.pinned) {
        body.pinned = true
      }
      return postNamedCommand('coreData', 'execute-redtail-domain-command', {
        command: 'createContactNote',
        autoResolveConnection: true,
        clientId,
        request: { contactId, body }
      })
    },
    onSuccess: () => {
      handleClose()
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.redtailDomainQuery, 'list-preset']
      })
    }
  })

  const submitError = error?.message || null

  return (
    <SydModal
      open={open}
      onClose={handleClose}
      title='Create Note'
      size='medium'
      disablePortal={false}
    >
      <form
        onSubmit={handleSubmit((values) => submitNote(values))}
        style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
      >
        <Controller
          name='contact'
          control={control}
          rules={{ required: 'Contact is required' }}
          render={({ field, fieldState }) => (
            <SydLabel label='Contact' required error={hookFormFieldErrorAdapter(fieldState)}>
              <SydAutocomplete
                placeholder='Select a contact...'
                options={familyOptions}
                value={field.value}
                onChange={(_e, val) => field.onChange(val)}
                renderOption={renderContactOption}
              />
            </SydLabel>
          )}
        />

        {categoryOptions.length > 1 ? (
          <Controller
            name='category_id'
            control={control}
            render={({ field }) => (
              <SydLabel label='Category'>
                <SydSelect
                  options={categoryOptions}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              </SydLabel>
            )}
          />
        ) : null}

        <Controller
          name='body'
          control={control}
          render={({ field }) => (
            <SydLabel label='Note Content' required>
              <div style={editorBorderStyle}>
                <WYSIWYGEditor
                  defaultValue={field.value}
                  onChange={(val) => field.onChange(val)}
                  placeholder='Write your note...'
                  isMarkdown={false}
                  enableAutofocus={false}
                />
              </div>
            </SydLabel>
          )}
        />

        <Controller
          name='pinned'
          control={control}
          render={({ field }) => (
            <SydCheckbox
              label='Pin this note'
              size='small'
              value={field.value}
              onChange={(_e, checked) => field.onChange(checked)}
            />
          )}
        />

        {submitError ? (
          <div style={{ color: 'var(--color-decline-500)', fontSize: 'var(--font-size-xs)' }}>
            {submitError}
          </div>
        ) : null}

        <SydModalActions>
          <SydButton
            variant='outline'
            size='sm'
            onClick={handleClose}
            type='button'
          >
            Cancel
          </SydButton>
          <SydButton
            variant='primary'
            size='sm'
            type='submit'
            processing={submitting}
            disabled={isBodyEmpty}
          >
            Create Note
          </SydButton>
        </SydModalActions>
      </form>
    </SydModal>
  )
}

CreateNoteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  config: PropTypes.object,
  members: PropTypes.array,
  existingNotes: PropTypes.array
}

export default CreateNoteModal
